import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaUsers, FaBuilding, FaHands, FaUniversity,
  FaCheckCircle, FaArrowRight
} from 'react-icons/fa';

// Import client logos
import kenyaRoadsBoard from '@assets/clients/Kenya-Roads-Board.jpg';
import dcl from '@assets/clients/DCL.jpg';
import uniqueLending from '@assets/clients/unique-lending.png';
import destinyWomen from '@assets/clients/destinywomen.jpg';
import amaco from '@assets/clients/Amaco-Africa.jpg';
import ujamii from '@assets/clients/Ujamii-Investments.jpg';
import barakaCredit from '@assets/clients/Baraka-Credit.png';
import firstCapital from '@assets/clients/first-capital.jpg';
import ontime from '@assets/clients/ontime.jpg';
import shofco from '@assets/clients/shofco.png';
import springboard from '@assets/clients/springboardlogo.png';

const clientLogos = [
  { name: 'Kenya Roads Board', logo: kenyaRoadsBoard, category: 'Government' },
  { name: 'Dream Credit', logo: dcl, category: 'Microfinance' },
  { name: 'Unique Lending Company', logo: uniqueLending, category: 'Microfinance' },
  { name: 'Destiny Women', logo: destinyWomen, category: 'NGO' },
  { name: 'Amaco Insurance', logo: amaco, category: 'Insurance' },
  { name: 'Ujamii Investments', logo: ujamii, category: 'Investment' },
  { name: 'Baraka Credit', logo: barakaCredit, category: 'Microfinance' },
  { name: 'First Capital', logo: firstCapital, category: 'Finance' },
  { name: 'On Time', logo: ontime, category: 'Logistics' },
  { name: 'Shofco', logo: shofco, category: 'NGO' },
  { name: 'Springboard Capital', logo: springboard, category: 'Investment' },
];

const clientCategories = [
  {
    title: 'SACCOs',
    icon: FaUniversity,
    count: '50+',
    description: 'Trusted by leading SACCOs across East Africa',
    color: 'from-amber-500 to-orange-500'
  },
  {
    title: 'Microfinance',
    icon: FaHands,
    count: '30+',
    description: 'Empowering microfinance institutions',
    color: 'from-emerald-500 to-teal-500'
  },
  {
    title: 'Banks & Finance',
    icon: FaBuilding,
    count: '25+',
    description: 'Serving major banks and financial institutions',
    color: 'from-blue-500 to-indigo-500'
  },
  {
    title: 'NGOs & Government',
    icon: FaUsers,
    count: '45+',
    description: 'Supporting non-profit and government organizations',
    color: 'from-purple-500 to-pink-500'
  },
];

const testimonials = [
  {
    name: 'Jane Mwangi',
    role: 'CEO, Bestrock SACCO',
    quote: 'Micropoint transformed our operations. Loan processing time reduced by 70%.',
    image: 'https://ui-avatars.com/api/?name=Jane+Mj&background=E8960D&color=fff&size=64'
  },
  {
    name: 'David Ochieng',
    role: 'Finance Manager, ABC Microfinance',
    quote: 'Excellent support and seamless MPESA integration. Customer engagement increased significantly.',
    image: 'https://ui-avatars.com/api/?name=David+O&background=074578&color=fff&size=64'
  },
  {
    name: 'Sarah Kariuki',
    role: 'IT Director, Unity Cooperative',
    quote: 'The ERP implementation was smooth and the team provided exceptional training.',
    image: 'https://ui-avatars.com/api/?name=Sarah+K&background=0a7e3a&color=fff&size=64'
  },
];

export default function Clients() {
  return (
    <div className="py-16 md:py-20 bg-gray-50">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block bg-micropoint-gold/10 text-micropoint-gold px-4 py-1 rounded-full text-sm font-semibold mb-3">
              Our Clients
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 font-display">
              Trusted by <span className="text-micropoint-gold">50+</span> Organizations
            </h1>
            <div className="w-20 h-1 bg-micropoint-gold mx-auto mt-4 rounded-full" />
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto text-sm md:text-base">
              We partner with organizations across East Africa to deliver innovative technology solutions
            </p>
          </motion.div>
        </div>

        {/* Client Logos Grid */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-1 h-8 bg-micropoint-gold rounded-full" />
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 font-display">Our Valued Clients</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
            {clientLogos.map((client, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="bg-white rounded-2xl p-4 border border-gray-200 hover:border-micropoint-gold hover:shadow-xl transition-all group flex items-center justify-center h-28 md:h-32"
              >
                <img
                  src={client.logo}
                  alt={client.name}
                  className="max-h-16 w-auto object-contain group-hover:scale-105 transition-all duration-300 filter grayscale hover:grayscale-0"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(client.name)}&background=E8960D&color=fff&size=64`;
                  }}
                />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Client Categories */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-1 h-8 bg-micropoint-gold rounded-full" />
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 font-display">Industries We Serve</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {clientCategories.map((category, index) => {
              const Icon = category.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-6 text-center border border-gray-200 hover:border-micropoint-gold hover:shadow-xl transition-all group"
                >
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${category.color} flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-all shadow-sm`}>
                    <Icon className="text-2xl text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-micropoint-gold">{category.count}</h3>
                  <h4 className="font-bold text-gray-900 text-lg">{category.title}</h4>
                  <p className="text-sm text-gray-600 mt-1">{category.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Testimonials */}
        {/* <div className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-1 h-8 bg-micropoint-gold rounded-full" />
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 font-display">What Our Clients Say</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-gray-200 hover:border-micropoint-gold transition-all shadow-sm hover:shadow-md"
              >
                <div className="flex items-center gap-4 mb-4">
                  <img src={testimonial.image} alt={testimonial.name} className="w-12 h-12 rounded-full" />
                  <div>
                    <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                  </div>
                </div>
                <div className="text-yellow-400 flex mb-2">
                  {[...Array(5)].map((_, i) => (
                    <span key={i}>⭐</span>
                  ))}
                </div>
                <p className="text-gray-600 text-sm italic">"{testimonial.quote}"</p>
              </motion.div>
            ))}
          </div>
        </div> */}

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-gradient-to-r from-micropoint-blue to-blue-700 rounded-2xl p-8 md:p-10 text-center text-white"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <FaCheckCircle className="text-3xl text-micropoint-gold" />
            <h2 className="text-2xl md:text-3xl font-bold font-display">Ready to Join Our Clients?</h2>
          </div>
          <p className="text-gray-200 mb-6 max-w-lg mx-auto">
            Experience the Micropoint difference. Let's transform your business together.
          </p>
          <Link to="/contact" className="bg-micropoint-gold hover:bg-yellow-500 text-white px-8 py-3 rounded-full font-semibold transition-all inline-flex items-center gap-2 hover:scale-105">
            Get Started <FaArrowRight />
          </Link>
        </motion.div>
      </div>
    </div>
  );
}